<script>
/**
 * ****************************************************************************
 * @description     :   Input DatePicker Component
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import flatPickr from 'vue-flatpickr-component';
import _ from 'lodash-uuid';
import { Japanese } from 'flatpickr/dist/l10n/ja.js';
// import moment from 'moment';

/**
 * Import template
 */
import template from './template.html';
import helpers from '@/utils/helpers';

/**
 * Define content of input datepicker component
 */
var AnsDateTime = {
    template: template,
    props: {
        id: String,
        label: String,
        name: String,
        itemClass: String,
        labelClass: String,
        wrapInputClass: {
            type: Object,
            default: () => ({})
        },
        inputClass: String,
        buttonClass: String,
        icon: String,
        placeholder: String,
        value: String,
        modelValue: String,
        isRequired: Boolean,
        isDisabled: Boolean,
        isReadonly: Boolean,
        isTimer: {
            type: Boolean,
            default: false
        },
        showIcon: {
            type: Boolean,
            default: false
        },
        isMonthYear: {
            type: Boolean,
            default: false
        },
        isYearOnly: {
            type: Boolean,
            default: false
        },
        isAllowMonthYear: {
            type: Boolean,
            default: false
        },
        autofocus: {
            type: Boolean,
            default: true
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        widthLabel: {
            type: String,
            default: 'width-110-px'
        },
        ceilMinus: {
            type: Number,
            default: 0
        },
        isAutocomplete: {
            type: Boolean,
            default: false
        },
        isAllowInput: { type: Boolean, default: true },
        onChangeEvent: Function,
        onFocusEvent: Function,
        onBlurEvent: Function,
        onClick: Function
    },
    components: {
        flatPickr
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isFocus: false,
            newId: '',
            isInputting: false,
            keyRender: 0,
            config_timer: {
                wrap: true,
                allowInput: this.isAllowInput,
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
                clickOpens: false,
                locale: Japanese,
                onChange: this.updateInput
            },
            modeDisabled: false
        };
    },
    computed: {
        vewValue: {
            get() {
                return this.getValue(this.modelValue ? this.modelValue : this.value);
            },
            set() {}
        },
        config_datetime() {
            return {
                wrap: true,
                // eslint-disable-next-line prettier/prettier
                altFormat: this.isMonthYear
                    ? 'Y/m'
                    : this.isYearOnly
                    ? 'Y'
                    : 'Y/m/d',
                altInput: true,
                // eslint-disable-next-line prettier/prettier
                dateFormat: this.isMonthYear
                    ? 'Y/m'
                    : this.isYearOnly
                    ? 'Y/m/d'
                    : 'Y/m/d',
                allowInput: this.isAllowInput,
                clickOpens: false,
                monthSelectorType: 'static',
                locale: Japanese,
                onChange: this.updateInput
            };
        },
        maxlength() {
            // eslint-disable-next-line prettier/prettier
            return this.isTimer
                ? 5
                : this.isYearOnly
                ? 4
                : this.isMonthYear
                ? 7
                : 10;
        }
    },
    mounted() {
        if (this.id && this.id != '') {
            this.newId = this.id;
        } else {
            this.newId = _.uuid();
        }
        this.modeDisabled = helpers.checkAuthorityMode(this.$route.name);
    },
    methods: {
        updateInput(_, val) {
            this.$emit('update:modelValue', val);
        },
        /**
         * convert date for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        roundUpMinute(minus) {
            if (this.ceilMinus == 0) {
                return minus;
            }
            if (minus < 10) {
                if (minus > 5) {
                    return '10';
                } else if (minus > 0 && minus <= 5) {
                    return '05';
                } else {
                    return '00';
                }
            } else if (minus > 10) {
                const firstMinus = minus.substring(0, 1);
                const lastMinus = minus.substring(1, 2);
                if (lastMinus > 5) {
                    return parseInt(firstMinus) + 1 + '0';
                } else if (lastMinus > 0 && lastMinus <= 5) {
                    return firstMinus + '5';
                } else {
                    return minus;
                }
            } else {
                return minus;
            }
        },
        convertTimeString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            var reg1 = /^([01][0-9]|2[0-3]|24)([0-5][0-9])$/;
            var reg2 = /^([01][0-9]|2[0-3]|24):([0-5][0-9])$/;
            var reg3 = /^([0-9])([0-5][0-9])$/;
            var reg4 = /^([0-5][0-9])$/;
            var reg5 = /^([0-9]):([0-5][0-9])$/;
            if (value.match(reg1)) {
                const minute = this.roundUpMinute(value.substring(2, 4));
                value = value.substring(0, 2) + ':' + minute;
            }
            if (value.match(reg3)) {
                const minute = this.roundUpMinute(value.substring(1, 3));
                value = '0' + value.substring(0, 1) + ':' + minute;
            }
            if (value.match(reg4)) {
                const minute = this.roundUpMinute(value);
                value = '00:' + minute;
            }
            if (!value.match(reg2) && !value.match(reg5)) {
                value = '';
            } else {
                const hour = value.substring(0, 2);
                const minute = value.substring(value.length - 2, value.length);
                if (hour == '24' && minute != '00') {
                    value = '';
                }
            }
            //
            return value;
        },
        /**
         * convert date for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertDateString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            var reg1 = /^[0-9]{8}$/;
            var reg2 = /^[0-9]{4}[/.][0-9]{2}[/.][0-9]{2}$/;
            if (value.match(reg1)) {
                value = value.substring(0, 4) + '/' + value.substring(4, 6) + '/' + value.substring(6);
            }
            if (!value.match(reg1) && !value.match(reg2)) {
                value = '';
            }
            if (!this.isDate(value)) {
                value = '';
            }
            return value;
        },
        /**
         * convert year/month for input datepicker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertYearMonthString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            var reg1 = /^[0-9]{6}$/;
            var reg2 = /^[0-9]{4}[/.][0-9]{2}$/;
            if (value.match(reg1)) {
                value = value.substring(0, 4) + '/' + value.substring(4, 6);
            }
            if (!value.match(reg1) && !value.match(reg2)) {
                value = '';
            }
            // if month > 12 => clear value
            if (value.substring(5, 7) && value.substring(5, 7) > 12) {
                value = '';
            }
            return value;
        },
        /**
         * convert year/month for input datepicker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertYearString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            value = value.substring(0, 4);
            return value;
        },
        /**
         * Check date type for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        isDate(string) {
            if (string == '') {
                return true;
            }
            if (string.length == 8) {
                string = string.substring(0, 4) + '/' + string.substring(4, 6) + '/' + string.substring(6);
            }
            var reg =
                /^((19|[2-9][0-9])[0-9]{2})[/.](0[13578]|1[02])[/.]31|((19|[2-9][0-9])[0-9]{2}[/.](01|0[3-9]|1[0-2])[/.](29|30))|((19|[2-9][0-9])[0-9]{2}[/.](0[1-9]|1[0-2])[/.](0[1-9]|1[0-9]|2[0-8]))|((((19|[2-9][0-9])(04|08|[2468][048]|[13579][26]))|2000)[/.](02)[/.]29)$/;
            if (string.match(reg)) {
                return true;
            } else {
                return false;
            }
        },
        /**
         * event keydown on input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputKeydown(e) {
            if (!this.isTimer) {
                if (
                    !(
                        (e.keyCode > 47 && e.keyCode < 58) ||
                        (e.keyCode > 95 && e.keyCode < 106) ||
                        e.keyCode == 116 ||
                        e.keyCode == 13 ||
                        e.keyCode == 46 ||
                        e.keyCode == 37 ||
                        e.keyCode == 39 ||
                        e.keyCode == 35 ||
                        e.keyCode == 36 ||
                        e.keyCode == 8 ||
                        e.keyCode == 9 ||
                        e.ctrlKey ||
                        e.keyCode == 229 ||
                        e.keyCode == 191
                    )
                ) {
                    event.preventDefault();
                    return false;
                }
            } else {
                if (
                    !(
                        (e.keyCode > 47 && e.keyCode < 58) ||
                        (e.keyCode > 95 && e.keyCode < 106) ||
                        e.keyCode == 116 ||
                        e.keyCode == 13 ||
                        e.keyCode == 46 ||
                        e.keyCode == 37 ||
                        e.keyCode == 39 ||
                        e.keyCode == 35 ||
                        e.keyCode == 36 ||
                        e.keyCode == 8 ||
                        e.keyCode == 9 ||
                        e.ctrlKey ||
                        e.keyCode == 229 ||
                        e.keyCode == 186
                    )
                ) {
                    event.preventDefault();
                    return false;
                }
            }
            return true;
        },
        onChange(e) {
            let val = this.getValue(e.target.value);
            if (val === '') {
                this.keyRender++;
            }
            if ((this.vewValue ?? '') != (val ?? '')) {
                this.isInputting = true;
                this.$emit('update:modelValue', val);
                if (this.onChangeEvent) {
                    this.onChangeEvent(
                        val,
                        // null,
                        this.$refs['input_' + this.newId]
                    );
                }
            }
        },
        onBlur(e) {
            this.isFocus = false;
            if (this.onBlurEvent) {
                this.onBlurEvent(e, this.$refs['input_' + this.newId]);
            }
        },
        onFocus(e) {
            this.isFocus = true;
            if (this.onFocusEvent) {
                this.onFocusEvent(e);
            }
            // if (this.isTimer) {
            //     e.target.value = e.target.value.replace(':', '');
            // }
        },
        getValue(input) {
            let val = '';
            if (this.isTimer) {
                val = this.convertTimeString(input);
                // if (this.isFocus && !this.isDisabled && !this.isReadonly) {
                //     val = val.replace(/:/g, '');
                // }
            } else if (this.isMonthYear || (this.isAllowMonthYear && input?.length < 8)) {
                val = this.convertYearMonthString(input);
            } else if (this.isYearOnly) {
                val = this.convertYearString(input);
            } else {
                val = this.convertDateString(input);
            }
            return val;
        }
    }
};
export default AnsDateTime;
</script>
